import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewEncapsulation, afterNextRender, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { timer, switchMap } from 'rxjs';
import { AIService } from '@workbuddy/ai/core/ai.service';
import { ButtonIconComponent, ButtonDisplayStyle } from '@workbuddy/ui/v2/components/button';
import { AgentPanelService } from '../../services/agent.panel.service';
import { BackgroundStreamManager } from '../../services/background.stream.manager';
import { AgentChatContainerComponent } from '../../components/chat.container/chat.container.component';
import { AgentThreadListComponent } from '../../components/thread.list/thread.list.component';
import { AgentAutomationsPlaceholderComponent } from '../../components/automations.placeholder/automations.placeholder.component';
import { AgentUtility } from '../../agent.utility';

import { loadLastOpenThreadId, writeLastOpenThreadId } from '../../agent.fullpage.memory';

import type { AgentThread } from '../../agent.types';

// Conversations sidebar open/collapsed state persists in localStorage so it survives
// navigating away and back to /agent (the component remounts), instead of resetting to
// collapsed every time. Defaults OPEN; only an explicit collapse is remembered.
const SIDEBAR_STORAGE_KEY = 'agent-fullpage-sidebar';

// Backfill thread titles still missing right after a page load — covers refreshing while Mastra's
// generateTitle was still running (the send-time poll is lost on reload, and nothing else re-checks).
// Backfill re-fetches the thread LIST (one request returns every title) rather than polling each
// thread individually, so the request count is a small fixed handful regardless of how many chats
// are untitled — see pollTitlesViaList.
const TITLE_BACKFILL_WINDOW_MS = 2 * 60 * 1000; // only chase titles for threads created in the last 2 min
const TITLE_BACKFILL_DELAY_MS = 2000;
const TITLE_BACKFILL_ATTEMPTS = 4;

function loadSidebarOpen(): boolean {

  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'collapsed';
  } catch {
    return true;
  }
}

// The last-opened conversation persists in localStorage (see agent.fullpage.memory) so
// returning to /agent — which loses the ?threadId query param when reached via the nav —
// restores the chat you were in instead of a blank New Chat. The bottom tab bar writes
// the same store, so the chat you last focused in either surface is the one fullscreen
// restores. A shared freshness window caps it so a long-idle thread starts fresh rather
// than reopening as an uncached (expensive) request; each visit renews the window.

/**
 * @deprecated The fullpage chat layout is being replaced by the in-shell
 * pseudo panel (see `agent-popup-chat` with `displayMode === 'pseudo'`).
 * This component and its `/agent` route will be removed in a follow-up.
 * The sidenav still routes here for now; that link is removed as part of
 * the deletion thread.
 */
@Component({
  selector: 'agent-fullpage-chat',
  standalone: true,
  imports: [FormsModule, AgentChatContainerComponent, AgentThreadListComponent, AgentAutomationsPlaceholderComponent, ButtonIconComponent],
  templateUrl: './fullpage.chat.component.html',
  styleUrls: ['./fullpage.chat.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '(dragenter)': 'onDragEnter($event)',
    '(dragleave)': 'onDragLeave($event)',
    '(dragover)': 'onDragOver($event)',
    '(drop)': 'onDrop($event)',
  },
})
export class AgentFullpageChatComponent implements OnInit, OnDestroy {

  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;

  private readonly aiService = inject(AIService);
  private readonly panelService = inject(AgentPanelService);
  private readonly router = inject(Router);
  private readonly backgroundManager = inject(BackgroundStreamManager);
  private readonly destroyRef = inject(DestroyRef);

  // ─── Route query param ──────────────────────────────────────────────────────

  private readonly $queryThreadId = injectQueryParams('threadId');

  // ─── State ──────────────────────────────────────────────────────────────────

  protected readonly $threads = signal<AgentThread[]>([]);
  protected readonly $activeThreadId = signal<string | null>(null);
  protected readonly $loading = signal(false);
  protected readonly $sidebarOpen = signal(loadSidebarOpen());
  protected readonly $sidebarAnimate = signal(false);

  // After a click-collapse the cursor is still over the sidebar, which would
  // otherwise trip the hover-expand and immediately re-open it. Suppress the
  // expand until the pointer leaves the collapsed rail, so clicking Collapse
  // just collapses — no lingering re-open.
  protected readonly $peekSuppressed = signal(false);

  // Whether the collapsed rail is being hovered over its ICON ITEMS (not the
  // header arrow) — that's what slides the sidebar back open on hover. Hovering
  // the arrow is deliberately excluded so it doesn't fight the click-to-collapse.
  protected readonly $railPeek = signal(false);
  protected readonly $threadStates = this.backgroundManager.$threadStates;

  // Sidebar nav state. Search is owned here (not by the thread list) so the
  // "Search chats" nav button drops the search box down on demand. Automations
  // swaps the main panel to its placeholder view until that feature is built.
  protected readonly $searchActive = signal(false);
  protected readonly $searchQuery = signal('');
  protected readonly $automationsOpen = signal(false);

  private readonly searchInputRef = viewChild<ElementRef<HTMLInputElement>>('sidebarSearchInput');

  // The chat container, so dropped files (handled at the layout level, where the
  // overlay can also cover the agent sidebar) can be forwarded into the chat.
  private readonly chatContainerRef = viewChild(AgentChatContainerComponent);

  // ─── Drag & drop ──────────────────────────────────────────────────────────────
  // Owned here (not in the chat container) so the drag-over overlay covers the whole
  // agent surface — conversations sidebar included — but not the main app shell.

  protected readonly $isDragOver = signal(false);
  private dragCounter = 0;

  // The thread restored from storage on mount, pending reconciliation against the
  // loaded list (it may have been deleted in another session).
  private restoredThreadId: string | null = null;

  // Persist the active thread (with a timestamp) on every change so it can be
  // restored within the freshness window on a later visit. Writing null clears it,
  // so an explicit New Chat is remembered as a New Chat. Unlike the panel service's
  // guarded _persistTabs, the first run is intentionally NOT skipped — re-stamping
  // the timestamp on each mount is what renews the rolling idle window per visit.
  private readonly _persistActiveThread = effect(() => writeLastOpenThreadId(this.$activeThreadId()));

  // Tell the root panel service exactly which fullscreen conversation is visible. Completion toasts
  // can then suppress only that chat while still appearing when a different fullscreen chat finishes.
  private readonly _publishFullpageActiveThread = effect(() => {

    const visibleThreadId = this.$automationsOpen() ? null : this.$activeThreadId();
    this.panelService.setFullpageActiveThread(visibleThreadId);
  });

  // A completion toast can navigate from one /agent?threadId=... URL to another without remounting
  // this component. Keep the local owner in sync with that external query-param change.
  private readonly _syncExternalThreadNavigation = effect(() => {

    const queryThreadId = this.$queryThreadId();
    if (!queryThreadId || queryThreadId === untracked(() => this.$activeThreadId())) return;
    untracked(() => {
      this.resetSearch();
      this.$automationsOpen.set(false);
      this.$activeThreadId.set(queryThreadId);
    });
  });

  // Enable the sidebar width transition only after first paint, so a remembered
  // collapsed sidebar applies its width instantly instead of visibly sliding shut.
  private readonly _enableSidebarAnim = afterNextRender(() => this.$sidebarAnimate.set(true));

  // ─── Computed ───────────────────────────────────────────────────────────────

  protected readonly $activeThread = computed(() => {

    const id = this.$activeThreadId();
    if (!id) return null;
    return this.$threads().find(t => t.id === id) ?? null;
  });

  protected readonly $headerTitle = computed(() => {

    if (this.$automationsOpen()) return 'Automations';
    const thread = this.$activeThread();
    return thread?.title ?? 'New Chat';
  });

  // "New chat" is the active nav item whenever the main area is a blank new chat:
  // no thread open and not viewing automations.
  protected readonly $isNewChatActive = computed(() => !this.$automationsOpen() && !this.$activeThreadId());

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  public ngOnInit(): void {

    // Close popup panel when navigating to fullpage
    this.panelService.closePanel();

    // Fullpage has no shell-driven measure-and-reveal cycle — the chat is always
    // visible, so consumers gating on $panelVisible (e.g. focus) can run immediately.
    this.panelService.$panelVisible.set(true);

    // Load threads
    this.loadThreads();

    // Set the initial active thread: prefer an explicit ?threadId, else restore the
    // last-opened conversation if it's still within the freshness window.
    const queryThreadId = this.$queryThreadId();
    const threadId = queryThreadId ?? loadLastOpenThreadId();
    if (threadId) {
      // Only a storage-restored thread is reconciled against the loaded list (it may
      // have been deleted elsewhere). An explicit ?threadId deep-link is deliberate
      // intent, so let it attempt to load — and surface its own error if truly gone —
      // rather than being dropped when it sits past the 50-thread list fetch.
      if (!queryThreadId) this.restoredThreadId = threadId;
      this.$activeThreadId.set(threadId);
    }
  }

  public ngOnDestroy(): void {

    this.panelService.clearFullpageActiveThread();

    // Leaving /agent (nav away or minimize) collects the viewed thread into the
    // bottom tab bar so it stays reachable. Collect-only (no activation) so a
    // passive nav-away doesn't pop the panel open; the minimize button activates
    // the tab itself before navigating. A blank New Chat has no thread to collect.
    const threadId = this.$activeThreadId();
    const thread = this.$activeThread();
    if (threadId && thread) {
      this.panelService.collectTab(threadId, thread.title);
    }
  }

  // ─── Drag & drop ────────────────────────────────────────────────────────────

  // Only react to file drags; dragging text or page elements must not trip the overlay.
  private isFileDrag(event: DragEvent): boolean {

    const types = event.dataTransfer?.types;
    return !!types && Array.from(types).includes('Files');
  }

  protected onDragEnter(event: DragEvent): void {

    if (!this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    this.dragCounter++;
    this.$isDragOver.set(true);
  }

  protected onDragLeave(event: DragEvent): void {

    if (!this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.$isDragOver.set(false);
    }
  }

  protected onDragOver(event: DragEvent): void {

    if (!this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
  }

  protected onDrop(event: DragEvent): void {

    if (!this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    this.dragCounter = 0;
    this.$isDragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.chatContainerRef()?.ingestFiles(Array.from(files));
    }
  }

  // ─── Actions ────────────────────────────────────────────────────────────────

  protected toggleSidebar(): void {

    this.setSidebarOpen(!this.$sidebarOpen());
  }

  protected expandSidebar(): void {

    this.setSidebarOpen(true);
  }

  // Hovering the rail's icon items (below the header arrow) slides the sidebar
  // open. Kept off the header arrow so hovering it never re-opens after collapse.
  protected onRailPeekEnter(): void {

    this.$railPeek.set(true);
  }

  // Leaving the sidebar entirely ends the hover-expand and clears the post-collapse
  // suppression, so a later deliberate hover over the rail items opens it again.
  protected onSidebarPointerLeave(): void {

    this.$railPeek.set(false);
    this.$peekSuppressed.set(false);
  }

  private setSidebarOpen(open: boolean): void {

    // Collapsing via the button leaves the cursor on the sidebar; suppress the
    // hover-peek so the panel collapses cleanly instead of re-floating on top.
    this.$peekSuppressed.set(!open);
    this.$sidebarOpen.set(open);
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, open ? 'open' : 'collapsed');
    } catch {
      // Storage unavailable (quota / private mode) — skip persistence.
    }
  }

  // ─── Sidebar nav ──────────────────────────────────────────────────────────────

  protected toggleSearch(): void {

    const next = !this.$searchActive();
    this.$searchActive.set(next);
    if (next) {
      this.focusSearchInput();
    } else {
      this.$searchQuery.set('');
    }
  }

  // Triggered from the collapsed rail: expand the panel first so the search box is
  // visible, then activate and focus it.
  protected onSearchFromRail(): void {

    this.expandSidebar();
    this.$searchActive.set(true);
    this.focusSearchInput();
  }

  protected onSelectAutomations(): void {

    this.resetSearch();
    this.$automationsOpen.set(true);
  }

  // Collapse the search drop-down (and clear its query) so it doesn't stay open
  // sticky after the user moves on to another nav tab or opens a chat.
  private resetSearch(): void {

    this.$searchActive.set(false);
    this.$searchQuery.set('');
  }

  private focusSearchInput(): void {

    // The input is rendered by @if on $searchActive, so it only exists after the
    // next change-detection paint — defer the focus until the element is in the DOM.
    setTimeout(() => this.searchInputRef()?.nativeElement.focus());
  }

  protected onMinimize(): void {

    // Dock the current chat into the windowed popup, then leave /agent in a single
    // navigation so the popup (which only renders off /agent) appears with the thread
    // intact. Navigating to the remembered pre-/agent page — rather than location.back()
    // — avoids bouncing through stacked /agent?threadId history entries, each of which the
    // shell would tear the panel down on (the "click twice, then land on /home" bug).
    const threadId = this.$activeThreadId();
    if (threadId) {
      this.panelService.addTab(threadId, this.$activeThread()?.title ?? 'Chat');
    } else {
      this.panelService.openNewChat();
    }
    this.router.navigateByUrl(this.panelService.fullpageReturnUrl);
  }

  protected onThreadSelected(event: { threadId: string; title: string }): void {

    this.onSelectThread(event.threadId);
  }

  protected onSelectThread(threadId: string): void {

    this.resetSearch();
    this.$automationsOpen.set(false);
    this.$activeThreadId.set(threadId);
    this.router.navigate([], { queryParams: { threadId }, queryParamsHandling: 'merge' });
  }

  protected onNewChat(): void {

    this.resetSearch();
    this.$automationsOpen.set(false);
    this.$activeThreadId.set(null);
    this.router.navigate([], { queryParams: { threadId: null }, queryParamsHandling: 'merge' });
  }

  protected onThreadChanged(threadId: string): void {

    this.$activeThreadId.set(threadId);
    this.router.navigate([], { queryParams: { threadId }, queryParamsHandling: 'merge' });

    // If this is a new thread not already in the list, add it optimistically
    const alreadyInList = this.$threads().some(t => t.id === threadId);
    if (!alreadyInList) this.addOptimisticThread(threadId, 'Untitled chat');
  }

  protected onTitleUpdated(event: { threadId: string; title: string }): void {

    // A new chat's temporary title is published on send and can arrive before onThreadChanged has added the
    // chat, so an unknown thread is added here rather than the title being dropped.
    const alreadyInList = this.$threads().some(t => t.id === event.threadId);
    if (alreadyInList) this.$threads.update(threads => threads.map(t => t.id === event.threadId ? { ...t, title: event.title } : t));
    else this.addOptimisticThread(event.threadId, event.title);
    this.panelService.updateTabTitle(event.threadId, event.title);
  }

  // Puts a chat the server has not listed yet at the top of the sidebar; the next list load replaces it.
  private addOptimisticThread(threadId: string, title: string): void {

    const now = new Date().toISOString();
    const optimisticThread: AgentThread = {
      id: threadId,
      title,
      resourceId: AgentUtility.AGENT_ID,
      createdAt: now,
      updatedAt: now,
    };
    this.$threads.update(threads => [optimisticThread, ...threads]);
  }

  protected onDeleteThread(threadId: string): void {

    this.aiService.deleteThread(AgentUtility.AGENT_ID, threadId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.$threads.update(threads => threads.filter(t => t.id !== threadId));
        if (this.$activeThreadId() === threadId) {
          this.$activeThreadId.set(null);
          this.router.navigate([], { queryParams: { threadId: null }, queryParamsHandling: 'merge', replaceUrl: true });
        }
      });
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  // A remembered thread may have been deleted in another session; if the restored
  // id isn't in the loaded list, drop back to a New Chat instead of trying to open
  // a dead thread (which would surface a load error). Only ever touches the restored
  // value, so an optimistic just-created thread is never cleared.
  private reconcileRestoredThread(threads: AgentThread[]): void {

    const restored = this.restoredThreadId;
    this.restoredThreadId = null;
    if (!restored || threads.some(t => t.id === restored)) return;
    if (this.$activeThreadId() !== restored) return;
    this.$activeThreadId.set(null);
    this.router.navigate([], { queryParams: { threadId: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  private loadThreads(): void {

    this.$loading.set(true);
    this.aiService.listThreads(AgentUtility.AGENT_ID, 0, 50)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.$threads.set(response.threads);
          this.$loading.set(false);
          this.reconcileRestoredThread(response.threads);
          this.backfillMissingTitles(response.threads);
        },
        error: () => {
          this.$loading.set(false);
        },
      });
  }

  // After a page load, chase titles for any recently-created threads that still don't have one:
  // if the user refreshed while generateTitle was running, the send-time poll was lost and nothing
  // else re-checks. Only kicks off if at least one recent thread is untitled; the poll itself
  // re-fetches the whole list (one request covers every title), so cost is fixed, not per-thread.
  private backfillMissingTitles(threads: AgentThread[]): void {

    const now = Date.now();
    const hasRecentUntitled = threads.some(t => !t.title && !!t.createdAt && now - new Date(t.createdAt).getTime() < TITLE_BACKFILL_WINDOW_MS);
    if (hasRecentUntitled) this.pollTitlesViaList(TITLE_BACKFILL_ATTEMPTS);
  }

  // Re-fetch the thread list on an interval and merge in any titles that have since been generated.
  // One request returns every thread's title, so N untitled chats cost ~1 request per attempt (not
  // 8 per chat). Stops as soon as no recent thread is untitled, or after a few attempts.
  private pollTitlesViaList(attemptsLeft: number): void {

    timer(TITLE_BACKFILL_DELAY_MS)
      .pipe(switchMap(() => this.aiService.listThreads(AgentUtility.AGENT_ID, 0, 50)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const freshTitleById = new Map(response.threads.filter(t => !!t.title).map(t => [t.id, t.title as string]));
          // Apply titles only to threads we're still showing as untitled (leaves everything else untouched).
          const newlyTitled = this.$threads().filter(t => !t.title && freshTitleById.has(t.id)).map(t => ({ id: t.id, title: freshTitleById.get(t.id) as string }));
          if (newlyTitled.length > 0) {
            const titleById = new Map(newlyTitled.map(n => [n.id, n.title]));
            this.$threads.update(threads => threads.map(t => titleById.has(t.id) ? { ...t, title: titleById.get(t.id) as string } : t));
            newlyTitled.forEach(({ id, title }) => this.panelService.updateTabTitle(id, title));
          }

          const now = Date.now();
          const stillMissing = response.threads.some(t => !t.title && !!t.createdAt && now - new Date(t.createdAt).getTime() < TITLE_BACKFILL_WINDOW_MS);
          if (stillMissing && attemptsLeft > 1) this.pollTitlesViaList(attemptsLeft - 1);
        },
        // Never retry into a rate limit: retrying on 429 is what turns a transient throttle into a
        // self-sustaining storm. Abort — titles backfill on the next natural load.
        error: (err) => {
          if ((err as { status?: number } | null)?.status === 429) return;
          if (attemptsLeft > 1) this.pollTitlesViaList(attemptsLeft - 1);
        },
      });
  }
}
